<template>
    <div class="article-page">
        <template v-if="article">
            <!-- the title on the charcoal band, the photo framed below it -->
            <header class="article-head">
                <span class="article-head__backdrop" :style="{ backgroundImage: `url('${image}')` }" />
                <div class="article-head__inner">
                    <router-link :to="{ name: 'News' }" class="article-head__back">
                        <v-icon small>mdi-arrow-left</v-icon>Όλα τα νέα
                    </router-link>
                    <div class="article-head__meta">
                        <span class="cl-chip cl-chip--gradient">News</span>
                        <span class="article-head__date"><v-icon x-small>mdi-calendar-outline</v-icon>{{ date }}</span>
                        <span v-if="readingMinutes" class="article-head__date"><v-icon x-small>mdi-clock-outline</v-icon>{{ readingMinutes }}' ανάγνωση</span>
                    </div>
                    <h1 class="article-head__title">{{ article.title }}</h1>
                </div>
            </header>

            <div class="article-body" :class="{ 'article-body--no-cover': !article.image }">
                <figure v-if="article.image" class="article-cover">
                    <span class="article-cover__backdrop" :style="{ backgroundImage: `url('${image}')` }" />
                    <img :src="image" :alt="article.title" class="article-cover__image">
                </figure>

                <div class="article-content" v-html="article.content" />

                <section v-if="article.gallery && article.gallery.length" class="article-gallery">
                    <div class="article-gallery__head">
                        <span class="article-gallery__title">Gallery</span>
                        <span class="article-gallery__count">{{ article.gallery.length }} φωτογραφίες</span>
                    </div>
                    <div class="article-gallery__grid">
                        <button
                            v-for="(galleryImage, index) in visibleGallery"
                            :key="index"
                            type="button"
                            class="article-gallery__item"
                            @click="openGallery(index)"
                        >
                            <img :src="getGalleryImage(galleryImage)" alt="" loading="lazy">
                            <span class="article-gallery__zoom"><v-icon color="white">mdi-magnify-plus-outline</v-icon></span>
                        </button>
                    </div>
                    <div v-if="article.gallery.length > visibleGallery.length" class="article-gallery__all">
                        <button type="button" class="article-gallery__all-btn" @click="showAllGallery = true">
                            <v-icon small>mdi-image-multiple-outline</v-icon>Δείτε όλες τις φωτογραφίες ({{ article.gallery.length }})
                        </button>
                    </div>
                </section>
            </div>

            <section v-if="moreArticles.length" class="cl-page article-more">
                <section-heading title="Περισσότερα νέα" icon="mdi-newspaper-variant-outline" />
                <div class="article-more__grid">
                    <article-card v-for="item in moreArticles" :key="item.id" :article="item" />
                </div>
            </section>

            <v-dialog v-model="galleryDialog" max-width="1100" content-class="article-lightbox">
                <v-carousel
                    v-if="galleryDialog"
                    v-model="galleryIndex"
                    hide-delimiter-background
                    :height="$vuetify.breakpoint.mobile ? 320 : 640"
                >
                    <v-carousel-item v-for="(galleryImage, index) in article.gallery" :key="index">
                        <div class="gallery-slide">
                            <div class="gallery-slide-backdrop" :style="`background-image: url('${getGalleryImage(galleryImage)}');`" />
                            <img :src="getGalleryImage(galleryImage)" class="gallery-slide-image" alt="">
                        </div>
                    </v-carousel-item>
                </v-carousel>
            </v-dialog>
        </template>

        <div v-else class="cl-page">
            <v-skeleton-loader type="image, article" />
        </div>
    </div>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import articleService from '@/services/article'
    import ArticleCard from '@/components/ArticleCard'
    import SectionHeading from '@/components/SectionHeading'

    export default {
        components: { ArticleCard, SectionHeading },
        data() {
            return {
                article: null,
                latest: [],
                galleryDialog: false,
                galleryIndex: 0,
                showAllGallery: false
            }
        },
        computed: {
            image() {
                return `https://storage.googleapis.com/${this.article.image}`
            },
            date() {
                return this.article.date ? momentTimezone(this.article.date).tz('Europe/Athens').locale('el').format('dddd D MMMM YYYY') : ''
            },
            // about 200 words a minute
            readingMinutes() {
                if (!this.article.content) return 0
                const text = new DOMParser().parseFromString(this.article.content, 'text/html').body.textContent || ''
                const words = text.trim().split(/\s+/).filter(Boolean).length
                return words > 60 ? Math.max(1, Math.round(words / 200)) : 0
            },
            // a big gallery opens with its first photos; the lightbox still goes through all of them
            visibleGallery() {
                const gallery = this.article.gallery || []
                return this.showAllGallery ? gallery : gallery.slice(0, 12)
            },
            moreArticles() {
                return this.latest.filter(item => String(item.id) !== String(this.$route.params.id)).slice(0, 3)
            }
        },
        watch: {
            '$route.params.id': {
                handler() {
                    this.showAllGallery = false
                    this.getArticle()
                    window.scrollTo(0, 0)
                }
            }
        },
        created() {
            this.getArticle()
            articleService.getArticles(1, 4).then(resp => {
                this.latest = resp.data.content || []
            }).catch(() => {})
        },
        methods: {
            getGalleryImage(image) {
                return `https://storage.googleapis.com/${image}`
            },
            openGallery(index) {
                this.galleryIndex = index
                this.galleryDialog = true
            },
            getArticle() {
                articleService.getArticle(this.$route.params.id).then(resp => {
                    this.article = resp.data
                })
            }
        }
    }
</script>

<style scoped>
/* ---------- head ---------- */
.article-head {
    position: relative;
    overflow: hidden;
    padding: 34px 24px 120px;
    background: #232323;
    color: #fff;
}

.article-head__backdrop {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(40px) brightness(0.35) saturate(1.2);
    transform: scale(1.2);
}

.article-head::after {
    content: '';
    position: absolute;
    inset: 0;
    background: radial-gradient(circle at 85% 0%, rgba(3, 229, 183, 0.22) 0%, rgba(3, 229, 183, 0) 55%);
}

.article-head__inner {
    position: relative;
    z-index: 1;
    max-width: 900px;
    margin: 0 auto;
    text-align: left;
}

.article-head__back {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 22px;
    color: rgba(255, 255, 255, 0.75) !important;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    text-decoration: none;
}

.article-head__back .v-icon {
    color: inherit !important;
    transition: transform 0.15s;
}

.article-head__back:hover {
    color: #03e5b7 !important;
}

.article-head__back:hover .v-icon {
    transform: translateX(-3px);
}

.article-head__meta {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 8px 14px;
    margin-bottom: 14px;
}

.article-head__date {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.82rem;
    color: rgba(255, 255, 255, 0.75);
    text-transform: capitalize;
}

.article-head__date .v-icon {
    color: #03e5b7 !important;
}

.article-head__title {
    margin: 0;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 3rem;
    font-weight: 400;
    line-height: 1.04;
    letter-spacing: 0.01em;
}

/* ---------- body ---------- */
.article-body {
    position: relative;

    /* 900px of content, as the title above */
    max-width: 948px;
    margin: -96px auto 0;
    padding: 0 24px;
}

.article-cover {
    position: relative;
    margin: 0 0 34px;
    aspect-ratio: 16 / 9;
    border-radius: 14px;
    background: #232323;
    box-shadow: 0 20px 44px rgba(35, 35, 35, 0.28);
    overflow: hidden;
}

.article-cover::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    height: 4px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.article-cover__backdrop {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(22px) brightness(0.6);
    transform: scale(1.15);
}

.article-cover__image {
    position: relative;
    display: block;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/* the text of the article: a calm column to read */
.article-content {
    max-width: 760px;
    margin: 0 auto;
    font-family: Roboto, Arial, sans-serif;
    font-size: 1.08rem;
    line-height: 1.8;
    color: #2b2b2b;
    text-align: left;
    overflow-wrap: break-word;
}

.article-content ::v-deep p {
    margin-bottom: 1.1em;
}

.article-content ::v-deep h1,
.article-content ::v-deep h2,
.article-content ::v-deep h3,
.article-content ::v-deep h4 {
    margin: 1.4em 0 0.5em;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-weight: 400;
    line-height: 1.1;
    color: #232323;
}

.article-content ::v-deep h2 {
    font-size: 2rem;
}

.article-content ::v-deep h3 {
    font-size: 1.6rem;
}

.article-content ::v-deep a {
    color: #009688;
    font-weight: 600;
}

.article-content ::v-deep strong {
    color: #232323;
}

.article-content ::v-deep blockquote {
    margin: 1.4em 0;
    padding: 6px 0 6px 18px;
    border-left: 4px solid #03e5b7;
    font-size: 1.15rem;
    font-style: italic;
    color: #232323;
}

.article-content ::v-deep ul,
.article-content ::v-deep ol {
    margin-bottom: 1.1em;
    padding-left: 1.4em;
}

/* the images and the media of the editor: never wider than the column */
.article-content ::v-deep img,
.article-content ::v-deep video,
.article-content ::v-deep embed,
.article-content ::v-deep object {
    display: block;
    max-width: 100% !important;
    height: auto !important;
    margin: 1.4em auto;
    border-radius: 10px;
}

.article-content ::v-deep iframe {
    display: block;
    width: 100% !important;
    max-width: 100% !important;
    height: auto !important;
    aspect-ratio: 16 / 9;
    margin: 1.4em auto;
    border: 0;
    border-radius: 10px;
}

.article-content ::v-deep figure {
    max-width: 100% !important;
    margin: 1.4em 0;
    overflow: hidden;
}

.article-content ::v-deep [width] {
    max-width: 100% !important;
}

.article-content ::v-deep table {
    width: 100% !important;
    table-layout: auto !important;
    border-collapse: collapse;
}

.article-content ::v-deep td,
.article-content ::v-deep th {
    padding: 6px 10px;
    border-bottom: 1px solid rgba(35, 35, 35, 0.1);
}

/* the sticky first column of the league tables is not for the tables of an article */
.article-content ::v-deep table > tbody > tr > td:nth-child(1),
.article-content ::v-deep table > thead > tr > th:nth-child(1) {
    position: static !important;
    background: transparent;
}

.article-body--no-cover {
    margin-top: 32px;
}

.article-head:has(+ .article-body--no-cover) {
    padding-bottom: 40px;
}

/* ---------- gallery ---------- */
.article-gallery {
    max-width: 900px;
    margin: 40px auto 0;
}

.article-gallery__head {
    display: flex;
    align-items: baseline;
    gap: 10px;
    margin-bottom: 14px;
}

.article-gallery__title {
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 1.8rem;
    text-transform: uppercase;
    color: #232323;
}

.article-gallery__count {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.82rem;
    color: rgba(35, 35, 35, 0.6);
}

.article-gallery__grid {
    display: grid;
    grid-template-columns: repeat(6, 1fr);
    gap: 10px;
}

.article-gallery__item {
    position: relative;
    display: block;
    aspect-ratio: 1;
    padding: 0;
    border-radius: 10px;
    background: #232323;
    overflow: hidden;
}

.article-gallery__item img {
    display: block;
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s;
}

.article-gallery__zoom {
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(3, 122, 222, 0.35);
    opacity: 0;
    transition: opacity 0.2s;
}

.article-gallery__item:hover img {
    transform: scale(1.06);
}

.article-gallery__item:hover .article-gallery__zoom {
    opacity: 1;
}

.article-gallery__all {
    display: flex;
    justify-content: center;
    margin-top: 16px;
}

.article-gallery__all-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 18px;
    border-radius: 8px;
    background: #232323;
    color: #fff;
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    box-shadow: inset 0 -3px 0 #03e5b7;
}

.article-gallery__all-btn .v-icon {
    color: #03e5b7 !important;
}

.article-gallery__all-btn:hover {
    background: #2e2e2e;
}

/* ---------- more news ---------- */
.article-more {
    margin-top: 56px;
}

.article-more__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(300px, 100%), 1fr));
    gap: 22px;
}

/* ---------- lightbox ---------- */
.gallery-slide {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
    background: #111;
}

.gallery-slide-backdrop {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(20px) brightness(0.5);
    transform: scale(1.1);
}

.gallery-slide-image {
    position: relative;
    height: 100%;
    width: 100%;
    object-fit: contain;
}

@media (max-width: 700px) {
    .article-head {
        padding: 22px 16px 80px;
    }

    .article-head__back {
        margin-bottom: 16px;
    }

    .article-head__title {
        font-size: 2rem;
    }

    .article-body {
        margin-top: -60px;
        padding: 0 16px;
    }

    .article-cover {
        margin-bottom: 24px;
        border-radius: 10px;
    }

    .article-content {
        font-size: 1rem;
        line-height: 1.7;
    }

    .article-gallery__grid {
        grid-template-columns: repeat(3, 1fr);
        gap: 6px;
    }
}
</style>
